import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { DashboardPage } from "./pages/DashboardPage";
import { ConversationsPage } from "./pages/ConversationsPage";
import { KnowledgeBasePage } from "./pages/KnowledgeBasePage";
import { TroubleshootingPage } from "./pages/TroubleshootingPage";
import { SettingsPage } from "./pages/SettingsPage";
import { WebhookLogsPage } from "./pages/WebhookLogsPage";
import { CRMPage } from "./pages/CRMPage";
import { PipelinePage } from "./pages/PipelinePage";
import { WhatsAppWebPage } from "./pages/WhatsAppWebPage";
import { KnowledgeHubPage } from "./pages/KnowledgeHubPage";
import { OutreachPage } from "./pages/OutreachPage";
import { OutreachDashboardPage } from "./pages/outreach/OutreachDashboardPage";
import { OutreachAISettingsPage } from "./pages/outreach/OutreachAISettingsPage";
import { OutreachAnalyticsPage } from "./pages/outreach/OutreachAnalyticsPage";
import { OutreachActivityLogsPage } from "./pages/outreach/OutreachActivityLogsPage";
import LoginPage from "./pages/LoginPage";
import { Loader2 } from "lucide-react";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

const TOAST_STYLE = {
  style: {
    background: 'hsl(240 6% 10%)',
    border: '1px solid hsl(240 4% 16%)',
    color: 'hsl(0 0% 98%)',
  },
};

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <Toaster
        position="top-right"
        richColors
        theme="dark"
        toastOptions={TOAST_STYLE}
      />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              {/* Root → Support dashboard */}
              <Route index element={<Navigate to="/support/dashboard" replace />} />

              {/* ─── SUPPORT AI ─────────────────────────────── */}
              <Route path="support">
                <Route index element={<Navigate to="/support/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="conversations" element={<ConversationsPage />} />
                <Route path="conversations/:conversationId" element={<ConversationsPage />} />
                <Route path="knowledge-hub" element={<KnowledgeHubPage />} />
                <Route path="knowledge" element={<KnowledgeBasePage />} />
                <Route path="troubleshooting" element={<TroubleshootingPage />} />
                <Route path="whatsapp" element={<WhatsAppWebPage />} />
                <Route path="logs" element={<WebhookLogsPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>

              {/* ─── OUTREACH AI ────────────────────────────── */}
              <Route path="outreach">
                <Route index element={<Navigate to="/outreach/dashboard" replace />} />
                <Route path="dashboard" element={<OutreachDashboardPage />} />
                <Route path="leads" element={<CRMPage />} />
                <Route path="pipeline" element={<PipelinePage />} />
                <Route path="campaigns" element={<OutreachPage />} />
                <Route path="logs" element={<OutreachActivityLogsPage />} />
                <Route path="analytics" element={<OutreachAnalyticsPage />} />
                <Route path="settings" element={<OutreachAISettingsPage />} />
              </Route>

              {/* ─── Legacy redirects (backward compatibility) ─── */}
              <Route path="conversations" element={<Navigate to="/support/conversations" replace />} />
              <Route path="conversations/:conversationId" element={<Navigate to="/support/conversations" replace />} />
              <Route path="knowledge" element={<Navigate to="/support/knowledge" replace />} />
              <Route path="knowledge-hub" element={<Navigate to="/support/knowledge-hub" replace />} />
              <Route path="troubleshooting" element={<Navigate to="/support/troubleshooting" replace />} />
              <Route path="whatsapp" element={<Navigate to="/support/whatsapp" replace />} />
              <Route path="logs" element={<Navigate to="/support/logs" replace />} />
              <Route path="settings" element={<Navigate to="/support/settings" replace />} />
              <Route path="crm" element={<Navigate to="/outreach/leads" replace />} />
              <Route path="pipeline" element={<Navigate to="/outreach/pipeline" replace />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
